import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { ColDef, ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IMatchOffer } from '@nkr/models/index';
import { Glyphicon } from 'react-bootstrap';
import { useMemo } from 'react';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: IMatchOffer[],
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {

    };

    type MyColDef = ColDef | ColGroupDef;

    const columns: MyColDef[] = [
        {
            colId: 'Rank',
            headerName: 'Rank',
            width: 55,
            resizable: true,
            suppressSizeToFit: true,
            headerClass: 'header-row-span-2',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
        },
        {
            groupId: 'recipient',
            headerName: 'Recipient',
            headerClass: 'group-header',
            children: [

                {
                    colId: 'recipientCenter',
                    field: 'recipientCenter',
                    headerName: 'Recipient Center',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientAlias',
                    field: 'recipientAlias',
                    headerName: 'Recipient Alias',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientCpra4',
                    field: 'recipientCpra4',
                    headerName: 'Recipient cPRA4',
                    width: 75,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>cPRA4</SortableHeaderComponent>,
                },
            ]
        },
        {
            groupId: 'donor',
            headerName: 'Donor',
            headerClass: 'group-header',
            children: [

                {
                    colId: 'donorCenter',
                    field: 'donorCenter',
                    headerName: 'Donor Center',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                },
                {
                    colId: 'donorAlias',
                    field: 'donorAlias',
                    headerName: 'Donor Alias',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                },
            ]
        },
        {
            colId: 'offerDateTime',
            field: 'offerDateTime',
            headerName: 'Offer',
            width: 120,
            headerClass: 'header-row-span-2',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Offer<br />Date/Time</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'deadlineDateTime',
            field: 'deadlineDateTime',
            headerName: 'Deadline',
            width: 120,
            headerClass: 'header-row-span-2',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Deadline<br />Date/Time</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            headerName: 'Decision',
            width: 120,
            headerClass: 'header-row-span-2',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decision<br />Date/Time</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            groupId: 'accepted',
            headerName: 'Accepted',
            headerClass: 'group-header',
            children: [

                {
                    colId: 'wasAcceptedNoIssues',
                    field: 'wasAcceptedNoIssues',
                    headerName: 'No Issues',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>No Issues</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasAcceptedNoIssues) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    colId: 'wasAcceptedNotConfident',
                    field: 'wasAcceptedNotConfident',
                    headerName: 'Not Confident',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Not<br />Confident</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasAcceptedNotConfident) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    colId: 'wasAcceptedNotConfidentAbove99Cpra',
                    field: 'wasAcceptedNotConfidentAbove99Cpra',
                    headerName: 'Not Confident > 99% NKR cPRA',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Not Confident<br /> &gt; 99% NKR cPRA</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasAcceptedNotConfidentAbove99Cpra) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
            ]
        },
        {
            groupId: 'declined',
            headerName: 'Declined',
            headerClass: 'group-header',
            children: [

                {
                    colId: 'wasDeclinedUnacceptable',
                    field: 'wasDeclinedUnacceptable',
                    headerName: 'Unacceptable',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Unacceptable</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasDeclinedUnacceptable) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    colId: 'wasDeclinedUnacceptableAbove99Cpra',
                    field: 'wasDeclinedUnacceptableAbove99Cpra',
                    headerName: 'Unaccepted > 99% NKR cPRA',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Unaccepted <br />&gt; 99% NKR cPRA</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasDeclinedUnacceptableAbove99Cpra) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    colId: 'wasDeclinedSurgeonUnavailable',
                    field: 'wasDeclinedSurgeonUnavailable',
                    headerName: 'Surgeon Unavailable',
                    width: 120,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Surgeon<br />Unavailable</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasDeclinedSurgeonUnavailable) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
                {
                    colId: 'wasDeclinedOther',
                    field: 'wasDeclinedOther',
                    headerName: 'Other',
                    width: 70,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Other</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IMatchOffer;

                        if (data.wasDeclinedOther) {
                            return (
                                <Glyphicon glyph='ok' />
                            )
                        }

                        return null;
                    }
                },
            ]
        },
        {
            colId: 'no-response',
            headerName: 'No Response',
            width: 85,
            resizable: true,
            suppressSizeToFit: true,
            headerClass: 'header-row-span-2-left-right',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>No<br />Response</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMatchOffer;

                if (data.decisionDateTime == '') {
                    return (
                        <Glyphicon glyph='ok' />
                    )
                }

                return null;
            }
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true,
                    resizable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                enableCellTextSelection={true}
                enableRangeSelection={true}
                headerHeight={44}
                noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                suppressCellSelection={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
            />
        </GridWrapper>
    );
}

export default Grid;